import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ClosedCaptioningContainer from './ClosedCaptioningContainer';
import PauseButton from './PauseButton';
import AudioInteractionOverlay from './AudioInteractionOverlay';
import { selectGameId, selectGameObject, selectGameStatusObject } from 'features/gamestate/gameStateSlice';
import { selectAllPlayers } from 'features/player/playerSlice';
import { selectAllQuestions } from 'features/question/questionSlice';
import { selectPlayerSubmissionsForJudging } from 'features/question/submissionSlice';
import { selectHostState, selectNarrations } from '../hostStateSlice';
import { handleStateChange } from '../hostOrchestrator';
import { computeDiff } from 'utils/diffUtils';
import { watchNarrationsAction, stopWatchingNarrationsAction } from '../hostStateSlice';
import logging from 'utils/logging';

const log = logging.getLogger('HostOverlay');
log.setLevel('info');

/**
 * Component that provides host services overlay functionality
 * This includes closed captioning and orchestration of host narration
 */
const HostOverlay = () => {
  const dispatch = useDispatch();
  
  // Game state and host state
  const gameId = useSelector(selectGameId);
  const gameObject = useSelector(selectGameObject);
  const gameState = useSelector(selectGameStatusObject);
  const players = useSelector(selectAllPlayers);
  const hostState = useSelector(selectHostState);
  const narrations = useSelector(selectNarrations);
  const questions = useSelector(selectAllQuestions);
  const submissions = useSelector(selectPlayerSubmissionsForJudging);
  
  // Keep track of previous game state
  const prevGameStateRef = useRef(null);
  
  useEffect(() => {
    if (gameId) {
      dispatch(watchNarrationsAction(gameId));
    }

    return function cleanup() {
      if (gameId) {
        dispatch(stopWatchingNarrationsAction(gameId));
      }
    };
  }, [gameId, dispatch]);
  
  // Watch for state changes and pass to orchestrator
  useEffect(() => {
    if (!gameId || !gameState) return;
    
    // Get previous game state
    const prevGameState = prevGameStateRef.current;
    
    // Update ref for next render
    prevGameStateRef.current = { ...gameState };

    const diff = computeDiff(prevGameState, gameState, { rootPath: 'game' });
    // log.debug(`State change detected: ${prevGameState?.currentState} -> ${gameState.currentState}`);
    log.debug(`State change diff`, diff);
    
    // Call orchestrator with all state data
    handleStateChange({
      gameId,
      prevGameState,
      currentGameState: gameState,
      currentHostState: hostState,
      players,
      questions,
      narrations,
      submissions,
      gameObject,
      dispatch
    });
    
  }, [gameId, gameState, hostState, players, narrations, questions, submissions, gameObject, dispatch]);
  
  return (
    <>
      <ClosedCaptioningContainer />
      <PauseButton />
      <AudioInteractionOverlay />
    </>
  );
};

export default HostOverlay; 